import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MdSearch, MdRefresh, MdOpenInNew, MdFilterList, MdClear,
  MdExpandMore, MdExpandLess, MdFileDownload, MdChevronLeft, MdChevronRight,
  MdArrowUpward, MdArrowDownward
} from 'react-icons/md';
import SkeletonRow from '../components/SkeletonRow';
import { helpdeskTicketApi, helpdeskCategoryApi, helpdeskLookupApi } from '../../../api/helpdeskApi';
import { toast } from 'react-toastify';
import '../Admin.css';
import './HelpdeskDashboard.css';

const STATUS_OPTIONS = ['OPEN', 'ASSIGNED', 'IN_PROGRESS', 'RESOLVED', 'CLOSED', 'REOPENED'];
const STATUS_LABELS = {
  OPEN: 'Open', ASSIGNED: 'Assigned', IN_PROGRESS: 'In Progress',
  RESOLVED: 'Resolved', CLOSED: 'Closed', REOPENED: 'Reopened'
};
const PRIORITY_OPTIONS = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];
const ID_CARD_STATUS_OPTIONS = ['SUBMITTED', 'DATA_VERIFIED', 'PHOTO_APPROVED', 'PRINT_QUEUE', 'PRINTING', 'PRINT_READY', 'DELIVERED', 'REJECTED'];
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

// Column definitions — common columns shared across all views
const COMMON_COLUMNS_START = [
  { key: 'index', label: '#', sortable: false, width: '40px' },
  { key: 'action', label: '', sortable: false, width: '40px' },
  { key: 'ticketNumber', label: 'Request ID', sortable: true },
  { key: 'createdAt', label: 'Date', sortable: true },
];

// General Request specific columns
const GENERAL_COLUMNS = [
  { key: 'title', label: 'Title', sortable: true },
  { key: 'category', label: 'Category', sortable: true },
  { key: 'subCategory', label: 'Sub Category', sortable: true },
  { key: 'priority', label: 'Priority', sortable: true },
  { key: 'city', label: 'City', sortable: true },
  { key: 'workLocation', label: 'Location', sortable: true },
  { key: 'floor', label: 'Floor', sortable: false },
  { key: 'requester', label: 'Requested By', sortable: true },
  { key: 'assignedTo', label: 'Assigned To', sortable: true },
  { key: 'ticketStatus', label: 'Status', sortable: true },
];

// ID Card Request specific columns
const ID_CARD_COLUMNS = [
  { key: 'title', label: 'Title', sortable: true },
  { key: 'employeeId', label: 'Emp ID', sortable: false },
  { key: 'priority', label: 'Priority', sortable: true },
  { key: 'city', label: 'City', sortable: true },
  { key: 'workLocation', label: 'Location', sortable: true },
  { key: 'floor', label: 'Floor', sortable: false },
  { key: 'requester', label: 'Requested By', sortable: true },
  { key: 'assignedTo', label: 'Assigned To', sortable: true },
  { key: 'ticketStatus', label: 'Status', sortable: true },
  { key: 'idCardStatus', label: 'ID Card Status', sortable: true },
];

// All types — combined view
const ALL_COLUMNS = [
  { key: 'title', label: 'Title', sortable: true },
  { key: 'requestType', label: 'Type', sortable: true },
  { key: 'category', label: 'Category', sortable: true },
  { key: 'priority', label: 'Priority', sortable: true },
  { key: 'city', label: 'City', sortable: true },
  { key: 'workLocation', label: 'Location', sortable: true },
  { key: 'floor', label: 'Floor', sortable: false },
  { key: 'requester', label: 'Requested By', sortable: true },
  { key: 'assignedTo', label: 'Assigned To', sortable: true },
  { key: 'ticketStatus', label: 'Status', sortable: true },
  { key: 'idCardStatus', label: 'ID Card Status', sortable: true },
];

function HelpdeskDashboard() {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  // Search with debounce
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const searchTimer = useRef(null);

  // Sorting
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');

  // Pagination
  const [currentPage, setCurrentPage] = useState(0); // 0-based for API
  const [pageSize, setPageSize] = useState(25);

  // Filters
  const [statusFilter, setStatusFilter] = useState('OPEN');
  const [priorityFilter, setPriorityFilter] = useState('ALL');
  const [categoryFilter, setCategoryFilter] = useState('ALL');
  const [cityFilter, setCityFilter] = useState('ALL');
  const [requestTypeFilter, setRequestTypeFilter] = useState('ALL');
  const [assignedFilter, setAssignedFilter] = useState('ALL');
  const [idCardStatusFilter, setIdCardStatusFilter] = useState('ALL');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Lookups
  const [categories, setCategories] = useState([]);
  const [cities, setCities] = useState([]);
  const [assignees, setAssignees] = useState([]);

  // Status counts (from separate all-tickets call for stat cards)
  const [statusCounts, setStatusCounts] = useState({});
  const [totalTicketCount, setTotalTicketCount] = useState(0);

  // Load lookup data + status counts once
  const loadLookups = useCallback(async () => {
    try {
      const [cats, cityList, adminList, statsData] = await Promise.all([
        helpdeskCategoryApi.getActive().catch(() => []),
        helpdeskLookupApi.getCities().catch(() => []),
        helpdeskLookupApi.getAdminUsers().catch(() => []),
        helpdeskTicketApi.getStatistics().catch(() => ({})),
      ]);
      setCategories(Array.isArray(cats) ? cats : []);
      setCities(Array.isArray(cityList) ? cityList : []);
      setAssignees(Array.isArray(adminList) ? adminList : []);
      setStatusCounts(statsData || {});
      const total = Object.values(statsData || {}).reduce((sum, v) => sum + (Number(v) || 0), 0);
      setTotalTicketCount(total);
    } catch (e) {
      console.error('Failed to load lookups:', e);
    }
  }, []);

  // Load paginated tickets
  const loadTickets = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await helpdeskTicketApi.getPaginated({
        page: currentPage,
        size: pageSize,
        sortBy,
        sortDir,
        status: statusFilter,
        priority: priorityFilter,
        requestType: requestTypeFilter,
        categoryName: categoryFilter,
        city: cityFilter,
        assignedTo: assignedFilter,
        idCardStatus: idCardStatusFilter,
        dateFrom,
        dateTo,
        search: searchQuery,
      });
      setTickets(Array.isArray(result.tickets) ? result.tickets : []);
      setTotalItems(result.totalItems || 0);
      setTotalPages(result.totalPages || 1);
    } catch (e) {
      console.error('Failed to load tickets:', e);
      toast.error('Failed to load tickets');
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, sortBy, sortDir, statusFilter, priorityFilter, requestTypeFilter, categoryFilter, cityFilter, assignedFilter, idCardStatusFilter, dateFrom, dateTo, searchQuery]);

  useEffect(() => {
    loadLookups();
  }, [loadLookups]);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  // Dynamic columns based on request type filter
  const columns = useMemo(() => {
    let typeCols;
    if (requestTypeFilter === 'GENERAL_REQUEST') {
      typeCols = GENERAL_COLUMNS;
    } else if (requestTypeFilter === 'ID_CARD_REQUEST') {
      typeCols = ID_CARD_COLUMNS;
    } else {
      typeCols = ALL_COLUMNS;
    }
    return [...COMMON_COLUMNS_START, ...typeCols];
  }, [requestTypeFilter]);

  // Debounced search
  const handleSearchChange = (value) => {
    setSearchInput(value);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setSearchQuery(value);
      setCurrentPage(0);
    }, 400);
  };

  // Reset page when filters change
  const applyFilter = (setter) => (value) => {
    setter(value);
    setCurrentPage(0);
  };

  // Column sort handler
  const handleSort = (columnKey) => {
    if (sortBy === columnKey) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(columnKey);
      setSortDir('asc');
    }
    setCurrentPage(0);
  };

  const hasActiveFilters = statusFilter !== 'ALL' || priorityFilter !== 'ALL' || categoryFilter !== 'ALL' ||
    cityFilter !== 'ALL' || requestTypeFilter !== 'ALL' || assignedFilter !== 'ALL' || idCardStatusFilter !== 'ALL' || dateFrom || dateTo;

  const activeFilterCount = [
    statusFilter !== 'ALL', priorityFilter !== 'ALL', categoryFilter !== 'ALL',
    cityFilter !== 'ALL', requestTypeFilter !== 'ALL', assignedFilter !== 'ALL',
    idCardStatusFilter !== 'ALL', !!dateFrom, !!dateTo
  ].filter(Boolean).length;

  const clearFilters = () => {
    setStatusFilter('ALL');
    setPriorityFilter('ALL');
    setCategoryFilter('ALL');
    setCityFilter('ALL');
    setRequestTypeFilter('ALL');
    setAssignedFilter('ALL');
    setIdCardStatusFilter('ALL');
    setDateFrom('');
    setDateTo('');
    setCurrentPage(0);
  };

  const handleRefresh = () => {
    loadLookups();
    loadTickets();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric'
    });
  };

  const exportToCSV = async () => {
    try {
      toast.info('Preparing export...');
      // Fetch ALL tickets (no pagination) for export
      const allTickets = await helpdeskTicketApi.getAll();
      if (!allTickets || allTickets.length === 0) {
        toast.info('No tickets to export');
        return;
      }
      // Sanitize CSV fields to prevent formula injection (=, +, -, @)
      const csvSafe = (val) => {
        const str = String(val || '');
        if (/^[=+\-@\t\r]/.test(str)) return "'" + str;
        return str;
      };
      const headers = ['Ticket #', 'Date', 'Title', 'Type', 'Category', 'Sub Category', 'Priority', 'City', 'Location', 'Floor', 'Requested By', 'Assigned To', 'Status'];
      const rows = allTickets.map(t => [
        csvSafe(t.ticketNumber),
        csvSafe(formatDate(t.createdAt)),
        csvSafe((t.title || '').replace(/,/g, ' ')),
        csvSafe(t.requestType === 'GENERAL_REQUEST' ? 'General' : 'ID Card'),
        csvSafe(t.category?.categoryName),
        csvSafe(t.subCategory?.subCategoryName),
        csvSafe(t.priority),
        csvSafe(t.city),
        csvSafe(t.workLocation?.locationName),
        csvSafe(t.floor),
        csvSafe(t.requester ? `${t.requester.firstName} ${t.requester.secondName}` : ''),
        csvSafe(t.assignedTo ? `${t.assignedTo.firstName} ${t.assignedTo.secondName}` : 'Unassigned'),
        csvSafe(t.ticketStatus?.replace('_', ' ')),
      ]);
      const csv = [headers.join(','), ...rows.map(r => r.map(c => `"${c}"`).join(','))].join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `helpdesk-tickets-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${allTickets.length} tickets`);
    } catch (e) {
      toast.error('Export failed');
    }
  };

  // Render sort indicator
  const renderSortIcon = (columnKey) => {
    if (sortBy !== columnKey) return <span className="sort-icon sort-inactive">⇅</span>;
    return sortDir === 'asc'
      ? <MdArrowUpward className="sort-icon sort-active" />
      : <MdArrowDownward className="sort-icon sort-active" />;
  };

  return (
    <div className="helpdesk-dashboard">
      {/* Header */}
      <div className="page-header">
        <div className="header-left">
          <h2>Helpdesk Tickets</h2>
          {(hasActiveFilters || searchQuery) && (
            <span className="result-count">{totalItems} result{totalItems !== 1 ? 's' : ''}</span>
          )}
        </div>
        <div className="header-actions">
          <button className="btn btn-export" onClick={exportToCSV} title="Export to CSV">
            <MdFileDownload /> Export
          </button>
          <button className="btn btn-secondary" onClick={handleRefresh} title="Refresh">
            <MdRefresh />
          </button>
        </div>
      </div>

      {/* Stat Cards — clickable status filter */}
      <div className="stats-row">
        <div
          className={`stat-card stat-total ${statusFilter === 'ALL' ? 'stat-active' : 'stat-dimmed'}`}
          onClick={() => applyFilter(setStatusFilter)('ALL')}
          style={{ cursor: 'pointer' }}
        >
          <div className="stat-value">{totalTicketCount}</div>
          <div className="stat-label">Total</div>
        </div>
        {STATUS_OPTIONS.map(status => (
          <div
            key={status}
            className={`stat-card stat-${status.toLowerCase()} ${statusFilter === status ? 'stat-active' : ''} ${statusFilter !== 'ALL' && statusFilter !== status ? 'stat-dimmed' : ''}`}
            onClick={() => applyFilter(setStatusFilter)(statusFilter === status ? 'ALL' : status)}
            style={{ cursor: 'pointer' }}
          >
            <div className="stat-value">{statusCounts[status] || 0}</div>
            <div className="stat-label">{STATUS_LABELS[status]}</div>
          </div>
        ))}
      </div>

      {/* Toolbar: Search + Filter toggle */}
      <div className="toolbar">
        <div className="search-box">
          <MdSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search by ticket #, title, requester, category..."
            value={searchInput}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
          {searchInput && (
            <button className="search-clear" onClick={() => { setSearchInput(''); setSearchQuery(''); setCurrentPage(0); }}>
              <MdClear />
            </button>
          )}
        </div>
        <div className="toolbar-right">
          <button
            className={`btn btn-filter-toggle ${showFilters ? 'active' : ''}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <MdFilterList />
            Filters
            {activeFilterCount > 0 && <span className="filter-count">{activeFilterCount}</span>}
            {showFilters ? <MdExpandLess /> : <MdExpandMore />}
          </button>
          {hasActiveFilters && (
            <button className="btn btn-clear-filters" onClick={clearFilters}>
              <MdClear /> Clear
            </button>
          )}
        </div>
      </div>

      {/* Collapsible Filters */}
      {showFilters && (
        <div className="filter-bar">
          <div className="filter-controls">
            <div className="filter-item">
              <select value={statusFilter} onChange={e => applyFilter(setStatusFilter)(e.target.value)}>
                <option value="ALL">All Status</option>
                {STATUS_OPTIONS.map(s => (
                  <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                ))}
              </select>
            </div>
            <div className="filter-item">
              <select value={priorityFilter} onChange={e => applyFilter(setPriorityFilter)(e.target.value)}>
                <option value="ALL">All Priorities</option>
                {PRIORITY_OPTIONS.map(p => (
                  <option key={p} value={p}>{p.charAt(0) + p.slice(1).toLowerCase()}</option>
                ))}
              </select>
            </div>
            <div className="filter-item">
              <select value={categoryFilter} onChange={e => applyFilter(setCategoryFilter)(e.target.value)}>
                <option value="ALL">All Categories</option>
                {categories.map(c => (
                  <option key={c.id} value={c.categoryName}>{c.categoryName}</option>
                ))}
              </select>
            </div>
            <div className="filter-item">
              <select value={cityFilter} onChange={e => applyFilter(setCityFilter)(e.target.value)}>
                <option value="ALL">All Cities</option>
                {cities.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div className="filter-item">
              <select value={requestTypeFilter} onChange={e => applyFilter(setRequestTypeFilter)(e.target.value)}>
                <option value="ALL">All Types</option>
                <option value="GENERAL_REQUEST">General Request</option>
                <option value="ID_CARD_REQUEST">ID Card Request</option>
              </select>
            </div>
            <div className="filter-item">
              <select value={assignedFilter} onChange={e => applyFilter(setAssignedFilter)(e.target.value)}>
                <option value="ALL">All Assignees</option>
                <option value="UNASSIGNED">Unassigned</option>
                {assignees.map(a => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
            </div>
            <div className="filter-item">
              <select value={idCardStatusFilter} onChange={e => applyFilter(setIdCardStatusFilter)(e.target.value)}>
                <option value="ALL">All ID Card Status</option>
                {ID_CARD_STATUS_OPTIONS.map(s => (
                  <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
                ))}
              </select>
            </div>
            <div className="filter-item">
              <input type="date" value={dateFrom} onChange={e => applyFilter(setDateFrom)(e.target.value)} title="From date" />
            </div>
            <div className="filter-item">
              <input type="date" value={dateTo} onChange={e => applyFilter(setDateTo)(e.target.value)} title="To date" />
            </div>
          </div>
        </div>
      )}

      {/* Request Type Tabs */}
      <div className="type-tabs">
        {[
          { value: 'ALL', label: 'All Tickets' },
          { value: 'GENERAL_REQUEST', label: 'General Requests' },
          { value: 'ID_CARD_REQUEST', label: 'ID Card Requests' },
        ].map(tab => (
          <button
            key={tab.value}
            className={`type-tab ${requestTypeFilter === tab.value ? 'active' : ''}`}
            onClick={() => applyFilter(setRequestTypeFilter)(tab.value)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Data Table */}
      <div className="data-table-container">
        <table className="data-table">
          <thead>
            <tr>
              {columns.map(col => (
                <th
                  key={col.key}
                  style={col.width ? { width: col.width } : undefined}
                  className={col.sortable ? 'sortable-th' : ''}
                  onClick={col.sortable ? () => handleSort(col.key) : undefined}
                >
                  <span className="th-content">
                    {col.label}
                    {col.sortable && renderSortIcon(col.key)}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              Array(8).fill(null).map((_, i) => <SkeletonRow key={i} cols={columns.length} />)
            ) : tickets.length === 0 ? (
              <tr><td colSpan={columns.length} className="empty-message">
                {hasActiveFilters || searchQuery ? 'No tickets match your filters' : 'No tickets found'}
              </td></tr>
            ) : (
              tickets.map((ticket, idx) => (
                <tr key={ticket.id} className="ticket-row" onClick={() => navigate(`/admin/helpdesk/tickets/${ticket.id}`)}>
                  {columns.map(col => {
                    switch (col.key) {
                      case 'index':
                        return <td key={col.key} className="row-num">{currentPage * pageSize + idx + 1}</td>;
                      case 'action':
                        return (
                          <td key={col.key}>
                            <button className="btn-icon btn-open" onClick={(e) => { e.stopPropagation(); navigate(`/admin/helpdesk/tickets/${ticket.id}`); }} title="View ticket">
                              <MdOpenInNew />
                            </button>
                          </td>
                        );
                      case 'ticketNumber':
                        return <td key={col.key} className="ticket-number">{ticket.ticketNumber}</td>;
                      case 'createdAt':
                        return <td key={col.key} className="date-cell">{formatDate(ticket.createdAt)}</td>;
                      case 'title':
                        return <td key={col.key} className="ticket-title">{ticket.title || '-'}</td>;
                      case 'requestType':
                        return (
                          <td key={col.key}>
                            <span className={`type-badge ${ticket.requestType === 'ID_CARD_REQUEST' ? 'type-idcard' : ''}`}>
                              {ticket.requestType === 'GENERAL_REQUEST' ? 'General' : 'ID Card'}
                            </span>
                          </td>
                        );
                      case 'category':
                        return <td key={col.key} className="category-cell">{ticket.category?.categoryName || '-'}</td>;
                      case 'subCategory':
                        return <td key={col.key} className="category-cell">{ticket.subCategory?.subCategoryName || '-'}</td>;
                      case 'priority':
                        return (
                          <td key={col.key}>
                            {ticket.priority ? (
                              <span className={`priority-badge priority-${ticket.priority.toLowerCase()}`}>{ticket.priority}</span>
                            ) : '-'}
                          </td>
                        );
                      case 'city':
                        return <td key={col.key}>{ticket.city || '-'}</td>;
                      case 'workLocation':
                        return <td key={col.key}>{ticket.workLocation?.locationName || '-'}</td>;
                      case 'floor':
                        return <td key={col.key}>{ticket.floor || '-'}</td>;
                      case 'employeeId':
                        return <td key={col.key}>{ticket.employeeId || '-'}</td>;
                      case 'requester':
                        return <td key={col.key} className="person-cell">{ticket.requester ? `${ticket.requester.firstName} ${ticket.requester.secondName}` : '-'}</td>;
                      case 'assignedTo':
                        return (
                          <td key={col.key} className="person-cell">
                            {ticket.assignedTo
                              ? `${ticket.assignedTo.firstName} ${ticket.assignedTo.secondName}`
                              : <span className="unassigned">Unassigned</span>
                            }
                          </td>
                        );
                      case 'ticketStatus':
                        return (
                          <td key={col.key}>
                            <span className={`status-badge status-${ticket.ticketStatus?.toLowerCase()}`}>
                              {STATUS_LABELS[ticket.ticketStatus] || ticket.ticketStatus?.replace('_', ' ')}
                            </span>
                          </td>
                        );
                      case 'idCardStatus':
                        return (
                          <td key={col.key}>
                            {ticket.requestType === 'ID_CARD_REQUEST' && ticket.idCardStatus ? (
                              <span className={`id-card-badge id-card-badge-${ticket.idCardStatus?.toLowerCase()}`}>
                                {ticket.idCardStatus?.replace('_', ' ')}
                              </span>
                            ) : '-'}
                          </td>
                        );
                      default:
                        return <td key={col.key}>-</td>;
                    }
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {!isLoading && totalItems > 0 && (
        <div className="pagination-bar">
          <div className="pagination-info">
            Showing {currentPage * pageSize + 1}–{Math.min((currentPage + 1) * pageSize, totalItems)} of {totalItems} tickets
          </div>
          <div className="pagination-controls">
            <select
              value={pageSize}
              onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(0); }}
              className="page-size-select"
            >
              {PAGE_SIZE_OPTIONS.map(s => (
                <option key={s} value={s}>{s} / page</option>
              ))}
            </select>
            <button className="btn-page" disabled={currentPage <= 0} onClick={() => setCurrentPage(p => p - 1)}>
              <MdChevronLeft />
            </button>
            <span className="page-num">{currentPage + 1} / {totalPages}</span>
            <button className="btn-page" disabled={currentPage >= totalPages - 1} onClick={() => setCurrentPage(p => p + 1)}>
              <MdChevronRight />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default HelpdeskDashboard;
